/**
 * QueuePromoteDialog — confirmation for admin displace-and-promote
 * (CU-86cb3x6y6, POST /reservations/queue/:id/promote).
 *
 * Only rendered when the caller holds BOTH reservation.manage_waitlist AND
 * reservation.force_release (see ReservationQueueListPermissions.canDisplacePromote
 * in reservation-queue-list.tsx) — this dialog is the confirmation step for that
 * action, not a permission gate itself.
 *
 * Shows the CURRENT active reservation (about to be force-released and deleted)
 * side by side with the NEW reservation that will be created for the chosen
 * queue entry, plus a plain-language list of consequences, so the admin cannot
 * mistake this for the low-stakes "remove from queue" action.
 *
 * Props-only component. No data fetching, no mutation calls.
 */

import { ArrowRight, AlertTriangle } from 'lucide-react';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import { cn } from '@ooh/ui/lib/utils';

const DIALOG_MAX_WIDTH = 'max-w-lg';

export interface QueuePromoteReservationSummary {
  holderUserName: string;
  clientName: string | null;
  offerName: string | null;
  statusLabel: string;
}

export interface QueuePromoteDialogLabels {
  title: string;
  subtitle: (mediaUnitName: string, dateFrom: string, dateTo: string) => string;
  currentCardTitle: string;
  nextCardTitle: (position: number) => string;
  holderLabel: string;
  clientLabel: string;
  offerLabel: string;
  statusLabel: string;
  noClientLabel: string;
  noOfferLabel: string;
  newStatusLabel: string;
  consequencesTitle: string;
  consequenceForceReleased: (holderUserName: string) => string;
  consequenceNewReservation: (holderUserName: string, dateFrom: string) => string;
  consequenceNotified: (holderUserName: string) => string;
  consequenceQueuePositionsKept: string;
  consequenceAudited: string;
  cancelLabel: string;
  confirmLabel: string;
}

export interface QueuePromoteDialogProps {
  mediaUnitName: string;
  dateFrom: string;
  dateTo: string;
  current: QueuePromoteReservationSummary;
  next: QueuePromoteReservationSummary;
  /** The chosen queue entry's FCFS position (e.g. 3 when Q3 is promoted while Q2 waits) — interpolated into nextCardTitle so the dialog never claims "position 1" for a non-head entry. */
  nextPosition: number;
  labels: QueuePromoteDialogLabels;
  isPending?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Narrow structural source for `buildPromoteCurrentSummary` — the active
 * reservation row about to be force-released. Both callers' row types
 * (`ReservationListItem`, `UnitReservationRow`) already carry these four
 * fields; declared here instead of importing either type to keep this
 * component free of route/feature-specific row shapes.
 */
export interface PromoteCurrentSummarySource {
  holderUserName: string;
  /** Optional: both callers carry it as an optional field, so the key may be absent entirely. */
  clientName?: string | null;
  offerName: string | null;
  statusLabel: string;
}

/**
 * Builds the QueuePromoteDialog "current" summary (the active reservation
 * about to be force-released) from the matched reservation row. Shared by
 * `/reservations` and `/media-units/$id` — previously duplicated identically
 * in both routes (CU-86cb7ctfw review fix, DRY).
 */
export function buildPromoteCurrentSummary(
  res: PromoteCurrentSummarySource,
): QueuePromoteReservationSummary {
  return {
    holderUserName: res.holderUserName,
    clientName: res.clientName ?? null,
    offerName: res.offerName,
    statusLabel: res.statusLabel,
  };
}

/** Narrow structural source for `buildPromoteNextSummary` — the chosen queue entry. */
export interface PromoteNextSummarySource {
  userName: string;
  /** Optional: `QueueEntryView` declares it optional, so the key may be absent entirely. */
  clientName?: string | null;
  offerName: string | null;
}

/**
 * Builds the QueuePromoteDialog "next" summary from the chosen queue entry.
 * Shared by `/reservations` and `/media-units/$id` — previously duplicated
 * identically in both routes (CU-86cb7ctfw review fix, DRY).
 */
export function buildPromoteNextSummary(
  entry: PromoteNextSummarySource,
  newStatusLabel: string,
): QueuePromoteReservationSummary {
  return {
    holderUserName: entry.userName,
    clientName: entry.clientName ?? null,
    offerName: entry.offerName,
    statusLabel: newStatusLabel,
  };
}

/**
 * Builds the full QueuePromoteDialogLabels object from a t() function.
 * Shared factory so the /media-units/$id route doesn't hand-roll the label
 * shape inline (CU-86cb3x6y6).
 *
 * @param t - Translation function from useTranslation('reservations')
 */
export function buildQueuePromoteDialogLabels(
  t: (key: string, opts?: Record<string, unknown>) => string,
): QueuePromoteDialogLabels {
  return {
    title: t('queuePromoteDialog.title'),
    subtitle: (mediaUnitName, dateFrom, dateTo) =>
      t('queuePromoteDialog.subtitle', { mediaUnitName, dateFrom, dateTo }),
    currentCardTitle: t('queuePromoteDialog.currentCardTitle'),
    nextCardTitle: (position) => t('queuePromoteDialog.nextCardTitle', { position }),
    holderLabel: t('queuePromoteDialog.holderLabel'),
    clientLabel: t('queuePromoteDialog.clientLabel'),
    offerLabel: t('queuePromoteDialog.offerLabel'),
    statusLabel: t('queuePromoteDialog.statusLabel'),
    noClientLabel: t('queuePromoteDialog.noClientLabel'),
    noOfferLabel: t('queuePromoteDialog.noOfferLabel'),
    newStatusLabel: t('queuePromoteDialog.newStatusLabel'),
    consequencesTitle: t('queuePromoteDialog.consequencesTitle'),
    consequenceForceReleased: (holderUserName) =>
      t('queuePromoteDialog.consequenceForceReleased', { holderUserName }),
    consequenceNewReservation: (holderUserName, dateFrom) =>
      t('queuePromoteDialog.consequenceNewReservation', { holderUserName, dateFrom }),
    consequenceNotified: (holderUserName) =>
      t('queuePromoteDialog.consequenceNotified', { holderUserName }),
    consequenceQueuePositionsKept: t('queuePromoteDialog.consequenceQueuePositionsKept'),
    consequenceAudited: t('queuePromoteDialog.consequenceAudited'),
    cancelLabel: t('queuePromoteDialog.cancelLabel'),
    confirmLabel: t('queuePromoteDialog.confirmLabel'),
  };
}

function SummaryCard({
  tone,
  title,
  summary,
  labels,
}: {
  tone: 'current' | 'next';
  title: string;
  summary: QueuePromoteReservationSummary;
  labels: QueuePromoteDialogLabels;
}) {
  return (
    <div
      data-testid={`queue-promote-card-${tone}`}
      className={cn(
        'flex-1 min-w-0 rounded-lg border px-3 py-2.5',
        tone === 'current'
          ? 'border-destructive/30 bg-destructive/5'
          : 'border-emerald-500/30 bg-emerald-500/5',
      )}
    >
      <p
        className={cn(
          'text-2xs font-semibold uppercase tracking-wide mb-2',
          tone === 'current' ? 'text-destructive' : 'text-emerald-700',
        )}
      >
        {title}
      </p>
      <dl className="space-y-1.5">
        <div>
          <dt className="text-2xs text-muted-foreground">{labels.holderLabel}</dt>
          <dd className="text-[13px] font-semibold truncate">{summary.holderUserName}</dd>
        </div>
        <div>
          <dt className="text-2xs text-muted-foreground">{labels.clientLabel}</dt>
          <dd className="text-[13px] truncate">{summary.clientName ?? labels.noClientLabel}</dd>
        </div>
        <div>
          <dt className="text-2xs text-muted-foreground">{labels.offerLabel}</dt>
          <dd className="text-[13px] truncate">{summary.offerName ?? labels.noOfferLabel}</dd>
        </div>
        <div>
          <dt className="text-2xs text-muted-foreground">{labels.statusLabel}</dt>
          <dd className="text-[13px]">{summary.statusLabel}</dd>
        </div>
      </dl>
    </div>
  );
}

export function QueuePromoteDialog({
  mediaUnitName,
  dateFrom,
  dateTo,
  current,
  next,
  nextPosition,
  labels,
  isPending = false,
  onClose,
  onConfirm,
}: QueuePromoteDialogProps) {
  return (
    <ModalDialog
      testId="queue-promote-dialog"
      maxWidth={DIALOG_MAX_WIDTH}
      header={
        <div>
          <span className="text-[15px] font-semibold">{labels.title}</span>
          <p className="text-xs text-muted-foreground font-normal mt-0.5">
            {labels.subtitle(mediaUnitName, dateFrom, dateTo)}
          </p>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
            {labels.cancelLabel}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            data-testid="queue-promote-confirm"
            onClick={onConfirm}
            disabled={isPending}
          >
            {labels.confirmLabel}
          </Button>
        </div>
      }
      onClose={onClose}
    >
      <div className="flex items-stretch gap-2 mb-4">
        <SummaryCard
          tone="current"
          title={labels.currentCardTitle}
          summary={current}
          labels={labels}
        />
        <div className="flex items-center text-muted-foreground shrink-0">
          <ArrowRight className="h-5 w-5" />
        </div>
        <SummaryCard
          tone="next"
          title={labels.nextCardTitle(nextPosition)}
          summary={{ ...next, statusLabel: labels.newStatusLabel }}
          labels={labels}
        />
      </div>

      <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-800 mb-1.5">
          <AlertTriangle className="h-3.5 w-3.5" />
          {labels.consequencesTitle}
        </p>
        <ul className="list-disc pl-4 space-y-1 text-xs text-amber-900/80">
          <li>{labels.consequenceForceReleased(current.holderUserName)}</li>
          <li>{labels.consequenceNewReservation(next.holderUserName, dateFrom)}</li>
          <li>{labels.consequenceNotified(current.holderUserName)}</li>
          <li>{labels.consequenceQueuePositionsKept}</li>
          <li>{labels.consequenceAudited}</li>
        </ul>
      </div>
    </ModalDialog>
  );
}
